import { useEffect, useState } from "react";
import Popup from "./popup";

const ImageCloudV2 = ({ images, pdf = false, customClickCallback }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [clickedImageUrl, setClickedImageUrl] = useState(null);
  const [renderingCloud, setRenderingCloud] = useState(true);

  const buildImageCloud = () => {
    const collage = document.getElementById("collage");
    if (!collage) return;
    const { width: collageWidth, height: collageHeight } = collage.getBoundingClientRect();

    let startingSize = window.innerWidth <= 768 ? 200 : 500;
    const padding = window.innerWidth <= 768 ? 2 : 15;
    const margin = window.innerWidth <= 768 ? 10 : 40;

    const checkCollision = (newItem, existingItems) => {
      const newRect = {
        left: parseFloat(newItem.style.left),
        top: parseFloat(newItem.style.top),
        width: newItem.clientWidth,
        height: newItem.clientHeight
      };

      for (const item of existingItems) {
        const itemRect = {
          left: parseFloat(item.style.left),
          top: parseFloat(item.style.top),
          width: item.clientWidth,
          height: item.clientHeight
        };

        if (
          newRect.left < itemRect.left + itemRect.width + padding &&
          newRect.left + newRect.width + padding > itemRect.left &&
          newRect.top < itemRect.top + itemRect.height + padding &&
          newRect.top + newRect.height + padding > itemRect.top
        ) {
          return true;
        }
      }
      return false;
    };

    const buildJigsawPattern = (items = {}) => {
      const center = {
        x: collageWidth / 2,
        y: collageHeight / 2
      };

      const centerItem = items[0].node;
      centerItem.style.left = `${center.x - centerItem.clientWidth / 2}px`;
      centerItem.style.top = `${center.y - centerItem.clientHeight / 2}px`;
      let placedItems = [centerItem];

      // Spiral search for best position
      const spiralSearch = (item) => {
        const step = 10;
        const maxRadius = Math.max(collageWidth, collageHeight);
        const collageRect = collage.getBoundingClientRect();
        for (let r = step; r < maxRadius; r += step) {
          for (let theta = 0; theta < 2 * Math.PI; theta += Math.PI / 16) {
            let x = center.x + r * Math.cos(theta) - item.clientWidth / 2;
            let y = center.y + r * Math.sin(theta) - item.clientHeight / 2;
            // Clamp to bounds
            x = Math.max(margin, Math.min(x, collageRect.width - margin - item.clientWidth));
            y = Math.max(margin, Math.min(y, collageRect.height - margin - item.clientHeight));
            item.style.left = `${x}px`;
            item.style.top = `${y}px`;
            if (!checkCollision(item, placedItems)) {
              return true;
            }
          }
        }
        return false;
      };

      for (let i = 1; i < items.length; i++) {
        const item = items[i].node;
        if (spiralSearch(item)) {
          placedItems.push(item);
        } else {
          // If any image can't be placed, shrink and retry
          return false;
        }
      }
      // Center after all placed
      centerTheImages();
      return true;
    };

    const sortItemsBySize = () => {
      const allCollageItems = document.querySelectorAll(".collage_item");
      if (allCollageItems.length === 0) return;
      const allItemsDescending = [];
      allCollageItems.forEach((item) => {
        const { clientWidth } = item;
        allItemsDescending.push({
          width: clientWidth,
          node: item
        });
      });
      allItemsDescending.sort((a, b) => parseFloat(b.width) - parseFloat(a.width));
      return allItemsDescending;
    };

    const checkOutOfBounds = () => {
      const collageContainer = document.getElementById("collage");
      const allCollageItems = collageContainer.querySelectorAll(".collage_item");

      const blueContainer = document.querySelector("div#collage");
      const blueRect = blueContainer.getBoundingClientRect();

      const bounds = {
        left: blueRect.left + margin,
        right: blueRect.right - margin,
        top: blueRect.top + margin,
        bottom: blueRect.bottom - margin,
        width: blueRect.width - 2 * margin,
        height: blueRect.height - 2 * margin,
        centerX: blueRect.left + blueRect.width / 2,
        centerY: blueRect.top + blueRect.height / 2
      };

      let isOutOfBounds = false;
      let totalArea = 0;
      let boundingBox = {
        minX: Infinity,
        maxX: -Infinity,
        minY: Infinity,
        maxY: -Infinity
      };

      for (const item of allCollageItems) {
        const itemRect = item.getBoundingClientRect();
        totalArea += (itemRect.width + padding) * (itemRect.height + padding);

        boundingBox.minX = Math.min(boundingBox.minX, itemRect.left);
        boundingBox.maxX = Math.max(boundingBox.maxX, itemRect.right);
        boundingBox.minY = Math.min(boundingBox.minY, itemRect.top);
        boundingBox.maxY = Math.max(boundingBox.maxY, itemRect.bottom);

        if (
          itemRect.left < bounds.left ||
          itemRect.right > bounds.right ||
          itemRect.top < bounds.top ||
          itemRect.bottom > bounds.bottom
        ) {
          isOutOfBounds = true;
          break;
        }
      }

      const usedWidth = boundingBox.maxX - boundingBox.minX;
      const usedHeight = boundingBox.maxY - boundingBox.minY;
      const spreadFactor = Math.max(usedWidth / bounds.width, usedHeight / bounds.height);
      const containerArea = bounds.width * bounds.height;
      const actualAreaRatio = totalArea / containerArea;

      return {
        isOutOfBounds,
        maxDistance: spreadFactor,
        allowedRadius: 0.9,
        areaRatio: actualAreaRatio,
        maxAreaRatio: 0.65
      };
    };

    const centerTheImages = () => {
      try {
        const allItems = document.querySelectorAll(".collage_item");
        const container = document.getElementById("collage");
        const containerRect = container.getBoundingClientRect();

        let minX = Infinity,
          maxX = -Infinity;
        let minY = Infinity,
          maxY = -Infinity;

        allItems.forEach((item) => {
          const rect = item.getBoundingClientRect();
          const left = rect.left - containerRect.left;
          const right = rect.right - containerRect.left;
          const top = rect.top - containerRect.top;
          const bottom = rect.bottom - containerRect.top;

          minX = Math.min(minX, left);
          maxX = Math.max(maxX, right);
          minY = Math.min(minY, top);
          maxY = Math.max(maxY, bottom);
        });

        const collageWidth = maxX - minX;
        const collageHeight = maxY - minY;

        const offsetX = (containerRect.width - collageWidth) / 2 - minX;
        const offsetY = (containerRect.height - collageHeight) / 2 - minY;

        allItems.forEach((item) => {
          const currentLeft = parseFloat(item.style.left) + offsetX || 0;
          const currentTop = parseFloat(item.style.top) + offsetY || 0;

          item.style.left = `${currentLeft - offsetX}px`;
          item.style.top = `${currentTop}px`;
        });
      } catch (error) {
        console.error("Unable to center images", error);
      } finally {
        setRenderingCloud(false);
        if (pdf) {
          collage.style.transformOrigin = "center";
          collage.style.transform = "scale(0.6) translateX(-5%) translateY(-5%)";
        }
        if (window.innerWidth <= 768) {
          collage.style.transform = "scale(0.9) translateX(0%) translateY(10%)";
        }
      }
    };

    const buildCollage = (items) => {
      if (!buildJigsawPattern(items)) {
        startingSize = startingSize - Math.max(5, Math.floor(startingSize * 0.15));
        if (startingSize >= 5) {
          initCollage(true);
        } else {
          centerTheImages();
        }
      }
    };

    const addHoverEffects = () => {
      const style = document.createElement("style");
      style.textContent = `
        .collage_item {
          transition: all 0.3s ease;
          z-index: 1;
        }
        
        @media (min-width: 768px) {
          .collage_item:hover {
            z-index: 40;
          }
          
          .collage_item[data-size="large"]:hover {
            transform: scale(1.2);
          }
          
          .collage_item[data-size="medium"]:hover {
            transform: scale(1.5);
          }
          
          .collage_item[data-size="small"]:hover {
            transform: scale(2);
          }

          .collage_item[data-size="extra-small"]:hover {
            transform: scale(3.5);
          }
        }
      `;
      document.head.appendChild(style);
    };

    const buildCollageHTML = (collage, images) => {
      collage.innerHTML = "";
      const sortedImages = images.sort((a, b) => b.score - a.score);
      const collageInner = document.createElement("div");
      collageInner.className = "collage_inner";

      const maxScore = Math.max(...images.map((img) => img.score));
      const baseSize = startingSize;

      const logScale = (score) => {
        const minSize = baseSize * 0.25;
        const maxSize = baseSize;
        const logScale = Math.pow(Math.log(score + 1) / Math.log(maxScore + 1), 2);
        return minSize + (maxSize - minSize) * logScale;
      };

      sortedImages.forEach(({ url, score }) => {
        const collageItem = document.createElement("div");
        collageItem.className = "collage_item";

        const scaledSize = logScale(score);
        collageItem.style.setProperty("--score", `${scaledSize}px`);

        if (scaledSize >= baseSize * 0.75) {
          collageItem.setAttribute("data-size", "large");
        } else if (scaledSize >= baseSize * 0.5) {
          collageItem.setAttribute("data-size", "medium");
        } else if (scaledSize >= baseSize * 0.35) {
          collageItem.setAttribute("data-size", "small");
        } else {
          collageItem.setAttribute("data-size", "extra-small");
        }

        const collageItemInner = document.createElement("div");
        collageItemInner.className = "collage_item_inner";

        const img = document.createElement("img");
        img.src = url;
        img.alt = "Selection";

        img.addEventListener("click", function () {
          if (customClickCallback) {
            customClickCallback(url);
            return;
          }
          setClickedImageUrl(url);
          setShowPopup(true);
        });

        collageItemInner.appendChild(img);
        collageItem.appendChild(collageItemInner);
        collageInner.appendChild(collageItem);
      });

      collage.appendChild(collageInner);
    };

    const initCollage = (shrink = false) => {
      if (shrink && startingSize >= 30) {
        startingSize = startingSize - Math.max(5, Math.floor(startingSize * 0.15));
      }

      buildCollageHTML(collage, images);
      addHoverEffects();

      const sorted = sortItemsBySize();
      buildCollage(sorted);
    };

    initCollage();
  };

  useEffect(() => {
    if (!images || !images?.length) return;

    buildImageCloud();

    window.addEventListener("resize", buildImageCloud);

    return () => {
      window.removeEventListener("resize", buildImageCloud);
    };
  }, [images]);

  return (
    <>
      <div id="collage"></div>
      {!pdf && (
        <Popup
          open={showPopup}
          setOpen={() => {
            setShowPopup(false);
            setClickedImageUrl(null);
          }}
          autoHeight={true}>
          <img src={clickedImageUrl} alt="" className="w-full" />
        </Popup>
      )}

      {renderingCloud && <span id="imageCloudRendering" />}
    </>
  );
};

export default ImageCloudV2;
